import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { EnumSelect } from "@/components/common/Pickers";
import { INVOICE_LINE_KINDS, label } from "@/lib/labels";
import { nok } from "@/lib/format";
import { cn } from "@/lib/utils";

const UNITS = ["mnd", "stk", "dogn", "time", "km"];
const UNIT_LABEL = { mnd: "mnd", stk: "stk", dogn: "døgn", time: "time", km: "km" };

export const emptyLine = (recurring = true) => ({ id: `tmp-${Math.random().toString(36).slice(2, 8)}`, kind: recurring ? "leie" : "etablering", description: "", quantity: 1, unit: recurring ? "mnd" : "stk", unit_price: 0, recurring });

/**
 * Line editor shared by invoices and contracts. `recurring` column only for contracts.
 * Amounts are eks. mva; totals shown below.
 */
export const LinesEditor = ({ lines, onChange, withRecurring = false, vatPct = 25, testId = "lines-editor" }) => {
  const set = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  const subtotal = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  return (
    <div data-testid={testId}>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11.5px] text-muted-foreground">
              <th className="pb-1.5 pr-2 font-medium">Type</th>
              <th className="pb-1.5 pr-2 font-medium">Beskrivelse</th>
              <th className="w-20 pb-1.5 pr-2 text-right font-medium">Antall</th>
              <th className="w-24 pb-1.5 pr-2 font-medium">Enhet</th>
              <th className="w-28 pb-1.5 pr-2 text-right font-medium">Pris</th>
              {withRecurring && <th className="w-16 pb-1.5 pr-2 text-center font-medium">Løpende</th>}
              <th className="w-28 pb-1.5 text-right font-medium">Beløp</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id || i} className="align-middle" data-testid="line-row">
                <td className="py-1 pr-2">
                  <EnumSelect value={l.kind} onChange={(v) => set(i, { kind: v })} options={INVOICE_LINE_KINDS} labelFn={label} testId={`line-kind-${i}`} className="h-8 w-[118px] text-[12.5px]" />
                </td>
                <td className="py-1 pr-2">
                  <Input value={l.description} onChange={(e) => set(i, { description: e.target.value })} placeholder="Beskrivelse" className="h-8 min-w-[200px] text-[12.5px]" data-testid={`line-description-${i}`} />
                </td>
                <td className="py-1 pr-2">
                  <Input type="number" step="0.01" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} className="h-8 text-right text-[12.5px] tabular" data-testid={`line-quantity-${i}`} />
                </td>
                <td className="py-1 pr-2">
                  <EnumSelect value={l.unit} onChange={(v) => set(i, { unit: v })} options={UNITS} labelFn={(u) => UNIT_LABEL[u] || u} testId={`line-unit-${i}`} className="h-8 text-[12.5px]" />
                </td>
                <td className="py-1 pr-2">
                  <Input type="number" step="1" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} className="h-8 text-right text-[12.5px] tabular" data-testid={`line-price-${i}`} />
                </td>
                {withRecurring && (
                  <td className="py-1 pr-2 text-center">
                    <Checkbox checked={!!l.recurring} onCheckedChange={(v) => set(i, { recurring: !!v, unit: v ? "mnd" : "stk" })} data-testid={`line-recurring-${i}`} aria-label="Løpende linje" />
                  </td>
                )}
                <td className="py-1 text-right tabular">{nok((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</td>
                <td className="py-1 pl-1">
                  <button type="button" onClick={() => remove(i)} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground" aria-label="Fjern linje" data-testid={`line-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...lines, emptyLine(withRecurring ? lines.length === 0 : false)])} data-testid="line-add-button">
          <Plus /> Legg til linje
        </Button>
        <div className="text-right text-[12.5px] text-muted-foreground">
          <div className={cn("tabular")}>Sum eks. mva <span className="ml-2 font-medium text-foreground">{nok(subtotal)}</span></div>
          <div className="tabular">Inkl. {vatPct} % mva <span className="ml-2 font-medium text-foreground">{nok(subtotal * (1 + vatPct / 100))}</span></div>
        </div>
      </div>
    </div>
  );
};
